/**
 * ACSolar design tokens.
 *
 * LIGHT system: the page ground is white and body copy is near-black. The dark
 * tones from the supplied palette (#081013 / #101B1F / #26383D) are used for
 * *dark sections* — the footer and any block whose ACF `theme` is "dark" — not
 * for the page itself.
 *
 * Everything below maps roles onto the names the component tree already
 * imports, so a token change here propagates without touching components.
 * When picking a colour: surfaces from `surface*`, copy from `text*`, and for
 * anything sitting on a dark section use `onDark*`.
 */

/** Brand ramp — 950 (deepest) → 50 (lightest). */
export const brand = {
  950: "#081013",
  900: "#1C262B",
  800: "#12363B",
  700: "#046471",
  600: "#0A7B88",
  500: "#1199A5",
  400: "#21B1BB",
  300: "#3BB9C1",
  200: "#83D7DC",
  100: "#C5EEF0",
  50: "#EAF9FA",
}

/** Status colours. Kept separate from the brand accent on purpose. */
export const semantic = {
  success: "#36C98F",
  warning: "#F2B84B",
  error: "#EF6262",
  info: "#5DB9E8",
}

/**
 * Role tokens.
 *
 * Legacy names (`primary`, `secondary`, `dark`, `accent`…) are kept because the
 * component tree imports them widely; their meanings are documented inline.
 */
export const palette = {
  /* Surfaces — lightest to deepest */
  background: "#FFFFFF", // page ground
  surface: "#F4F9FA", // cards, raised panels, menu overlay
  surfaceHover: brand[50], // hover state for the above
  border: "#D2E3E6", // hairlines, dividers, input outlines

  /* Text on light surfaces */
  text: brand[900], // primary copy
  textSecondary: "#3F5257", // supporting copy
  textMuted: "#61787D", // captions, disabled, meta
  white: "#FFFFFF",

  /* Dark sections (footer, theme="dark" blocks) */
  dark900: brand[950], // deepest dark ground
  darkSurface: "#101B1F",
  darkBorder: "#26383D",
  onDark: "#EDF7F8", // primary copy on a dark ground
  onDarkSecondary: "#B8C9CC",
  onDarkMuted: "#8FA5A9",

  /* Brand */
  primary: brand[800], // deep teal — footer ground, filled surfaces
  secondary: "#F4F9FA", // alias of `surface` (legacy name, used as a bg)
  accent: brand[600], // CTA fill on light — 5.6:1 with white ink
  accentDark: brand[700], // CTA hover
  accentLight: brand[500],

  /**
   * Legacy alias. Historically "the dark colour used for body text on a light
   * ground" — which is exactly its role again. Never use it as a background.
   */
  dark: brand[900],
}

/** RGB channels for rgba() composition. */
export const rgb = {
  background: "255, 255, 255",
  surface: "244, 249, 250",
  border: "210, 227, 230",
  primary: "18, 54, 59",
  secondary: "244, 249, 250",
  accent: "10, 123, 136",
  text: "28, 38, 43",
  dark: "28, 38, 43",
  onDark: "237, 247, 248",
  white: "255, 255, 255",
}

/** Build rgba from a palette key or raw channels. */
export const withAlpha = (channels, opacity) => `rgba(${channels}, ${opacity})`

/**
 * Pre-composed translucent tones.
 *
 * Alphas are chosen from measured contrast once composited over their intended
 * ground — don't lower them without re-measuring.
 */
export const alpha = {
  /** Full-page menu: light tint + backdrop blur. */
  menuOverlay: withAlpha(rgb.background, 0.92),
  menuOverlayLight: withAlpha(rgb.surface, 0.85),
  /** Fixed header bar. */
  headerBar: withAlpha(rgb.background, 0.92),
  /** Hairline on light surfaces. */
  hairline: withAlpha(rgb.text, 0.1),
  /** Accent wash behind badges/pills. */
  accentSoft: withAlpha(rgb.accent, 0.12),

  /** Ink tiers on a light ground (≈7.3:1 / ≈4.9:1 composited over white). */
  inkMuted: withAlpha(rgb.text, 0.75),
  inkFaint: withAlpha(rgb.text, 0.62),
  inkHairline: withAlpha(rgb.text, 0.18),

  /** Text tiers for use on a DARK section. */
  textSecondary: withAlpha(rgb.onDark, 0.75),
  textMuted: withAlpha(rgb.onDark, 0.62),
  textFaint: withAlpha(rgb.onDark, 0.45),
}

/** CMS page_settings.background aliases → palette keys. */
export const pageBackgroundMap = {
  default: "background",
  background: "background",
  white: "background",
  surface: "surface",
  dark: "dark900",
  // legacy miquinhas values, remapped onto the current system
  cream: "surface",
  beige: "surface",
}

/** page_settings.background (ACF select) → CSS colour. */
export const resolvePageBackground = (value) => {
  if (!value) return palette.background
  if (value.startsWith("#")) return value

  const key = value.toLowerCase()
  const paletteKey = pageBackgroundMap[key] || key

  return palette[paletteKey] || palette.background
}

/** Derived tokens for components & MUI. */
export const branding = {
  primary: palette.primary,
  secondary: palette.secondary,
  background: palette.background,
  muted: palette.textMuted,
  text: palette.text,
  active: brand[700], // pressed / hover for primary surfaces
  accent: palette.accent,
  accentActive: palette.accentDark,
  accentLight: palette.accentLight,
  black: palette.text,
  white: palette.white,
  border: palette.border,
  surface: palette.surface,
  surfaceHover: palette.surfaceHover,
  textSecondary: palette.textSecondary,
  onDark: palette.onDark,
}

/**
 * Neutral scale, lightest (neu01) → darkest (neu11).
 *
 * `TextContent.styles.js` picks `theme === "dark" ? neu01 : neu10`, so neu01
 * must be the light copy used on dark sections and neu10 the default body copy
 * on the light ground. `neu11` is referenced by article.styles.js and was
 * previously undefined.
 */
export const neutral = {
  neu01: palette.onDark,
  neu02: palette.surface,
  neu03: palette.surfaceHover,
  neu04: palette.border,
  neu05: "#A9C0C4",
  neu06: "#8399A0",
  neu07: palette.textMuted,
  neu08: palette.textSecondary,
  neu09: brand[800],
  neu10: palette.text,
  neu11: brand[950],
}

export const common = {
  success: semantic.success,
  warning: semantic.warning,
  error: semantic.error,
  info: semantic.info,
}

export const white = palette.white
export const black = { default: palette.text }
export const red = { default: semantic.error }

export default palette
